/**
 * Report DATA API (obj_type "R") — renders Jasper reports through
 * `/api/report-data/*` (pure-Python engine on the platform backend; the
 * deployed J2EE runtime serves the same contract with real JasperReports).
 */
import api from './client'
import type { CriteriaIssue, VisualCriteriaPromptVar } from '@twasta/page-runtime/txn'

export type ReportOutputFormat =
  | 'html' | 'pdf' | 'excel' | 'xlsx' | 'csv' | 'docx' | 'pptx' | 'rtf'

export interface ReportRequestBody {
  project: string
  obj_name?: string
  inline_model?: unknown
  jrxml_files?: Record<string, string>
  params?: Record<string, unknown>
  /** DIRECT $P{}-name-keyed values merged over the folded criteria (the
   *  embedded designer's own Generate dialog collects raw JRXML params). */
  jasper_params?: Record<string, unknown>
  output_format?: ReportOutputFormat
}

export interface ReportDefinitionResult {
  success: boolean
  promptVars: VisualCriteriaPromptVar[]
  resolvedParams: Record<string, unknown>
  needsInput: boolean
  missingParams: string[]
  defaultFormat: ReportOutputFormat
  formats: ReportOutputFormat[]
  title: string
}

export interface ReportValidateResult {
  success: boolean
  valid: boolean
  errors: Array<{ line: number; column: number; message: string }>
  warnings: Array<{ line: number; column: number; message: string }>
  parameters: Array<{
    name: string
    java_class: string
    default: string | null
    for_prompting: boolean
  }>
  report_name?: string
}

export interface ReportPreviewDataResult {
  success: boolean
  needsInput: boolean
  missingParams: string[]
  columns: string[]
  rows: Record<string, unknown>[]
  total_rows: number
}

/**
 * Re-throw an API failure carrying the backend's own `detail` text.
 *
 * The shared ReportViewer renders `error.message`, and axios sets that to a
 * bare "Request failed with status code 422" — so a perfectly explanatory
 * backend message ("The report engine (pyjasper) is not installed…",
 * "Report generation failed: ORA-00907…") reached the user as a status number
 * with nothing to act on. A blob-typed response (the /export download) holds
 * the JSON error as text, so it is read back before being unwrapped.
 */
async function withDetail<T>(call: () => Promise<T>): Promise<T> {
  try {
    return await call()
  } catch (e: unknown) {
    const res = (e as { response?: { data?: unknown } })?.response
    let data = res?.data
    if (data instanceof Blob) {
      try { data = JSON.parse(await data.text()) } catch { /* not JSON — keep axios' message */ }
    }
    const detail = (data as { detail?: unknown } | undefined)?.detail
    if (typeof detail === 'string' && detail.trim()) {
      throw new Error(detail)
    }
    throw e
  }
}

export async function fetchReportDefinition(
  body: ReportRequestBody,
): Promise<ReportDefinitionResult> {
  return withDetail(async () => {
    const res = await api.post('/report-data/definition', body)
    return res.data as ReportDefinitionResult
  })
}

/* ── Render jobs (submit → poll → download) ───────────────────────────────
 *
 * A big report can spend minutes inside the Jasper engine — longer than the
 * proxies between the browser and the backend will hold a response open, so
 * the synchronous /render and /export endpoints came back as a bare 504 while
 * the render was still working. Here every request is short: submit, poll a
 * cheap status endpoint, then download the finished bytes.
 *
 * Those synchronous endpoints still exist and are still the contract for the
 * downloadable runtime client and the J2EE renderer; the in-app viewer just
 * doesn't use them.
 */

export interface ReportJobStatus {
  job_id: string
  project: string
  obj_name: string | null
  status: 'queued' | 'running' | 'done' | 'failed'
  error: string | null
  needs_input: boolean
  missing_params: string[]
  /** Why the criteria were refused (mandatory / validations). */
  criteria_errors?: CriteriaIssue[]
  format: ReportOutputFormat
  mime: string | null
  filename: string | null
  size: number | null
  started_at: number
  finished_at: number | null
}

export interface ReportJobOutcome {
  needsInput: boolean
  missingParams: string[]
  criteriaErrors?: CriteriaIssue[]
  blob: Blob | null
  filename: string | null
}

/** Poll cadence: quick enough that a fast report feels synchronous, backing
 *  off so a multi-minute render isn't polled hundreds of times. */
const POLL_START_MS = 300
const POLL_MAX_MS = 2_000
const POLL_BACKOFF = 1.4
/** Give up waiting well after any real render, so a wedged job can't poll
 *  forever in a background tab. */
const POLL_LIMIT_MS = 15 * 60_000

export async function startReportJob(body: ReportRequestBody): Promise<string> {
  return withDetail(async () => {
    const res = await api.post('/report-data/render-job', body)
    return (res.data as { job_id: string }).job_id
  })
}

export async function fetchReportJobStatus(jobId: string): Promise<ReportJobStatus> {
  return withDetail(async () => {
    const res = await api.get(`/report-data/render-job/${encodeURIComponent(jobId)}`)
    return (res.data as { job: ReportJobStatus }).job
  })
}

export async function fetchReportJobContent(jobId: string): Promise<Blob> {
  return withDetail(async () => {
    const res = await api.get(
      `/report-data/render-job/${encodeURIComponent(jobId)}/content`,
      { responseType: 'blob' },
    )
    return res.data as Blob
  })
}

/**
 * Render a report end to end without holding a connection open: submit the
 * job, poll until it settles, then fetch the output.
 *
 * A failed job throws with the backend's own message ("Report generation
 * failed: ORA-00907…"), so callers surface errors exactly as they did when
 * the render was synchronous.
 */
export async function runReportJob(body: ReportRequestBody): Promise<ReportJobOutcome> {
  const jobId = await startReportJob(body)
  const deadline = Date.now() + POLL_LIMIT_MS
  let wait = POLL_START_MS

  for (;;) {
    await new Promise((r) => setTimeout(r, wait))
    const job = await fetchReportJobStatus(jobId)
    if (job.status === 'failed') {
      throw new Error(job.error || 'Report rendering failed')
    }
    if (job.status === 'done') {
      if (job.needs_input) {
        return {
          needsInput: true,
          missingParams: job.missing_params || [],
          criteriaErrors: job.criteria_errors || [],
          blob: null,
          filename: null,
        }
      }
      return {
        needsInput: false,
        missingParams: [],
        blob: await fetchReportJobContent(jobId),
        filename: job.filename,
      }
    }
    if (Date.now() > deadline) {
      throw new Error('The report is still rendering after 15 minutes — stopped waiting.')
    }
    wait = Math.min(Math.round(wait * POLL_BACKOFF), POLL_MAX_MS)
  }
}

export async function validateReportJrxml(body: {
  project: string
  obj_name?: string
  jrxml_content?: string
}): Promise<ReportValidateResult> {
  return withDetail(async () => {
    const res = await api.post('/report-data/validate', body)
    return res.data as ReportValidateResult
  })
}

export async function previewReportData(body: {
  project: string
  obj_name?: string
  inline_model?: unknown
  jrxml_content?: string
  params?: Record<string, unknown>
  limit?: number
}): Promise<ReportPreviewDataResult> {
  return withDetail(async () => {
    const res = await api.post('/report-data/preview-data', body)
    return res.data as ReportPreviewDataResult
  })
}
